import { TrashIcon, PencilSquareIcon } from '@heroicons/react/24/solid';
import {
  BaseEdge,
  useReactFlow,
  getBezierPath,
  type EdgeProps,
  getStraightPath,
  getSmoothStepPath,
  EdgeLabelRenderer,
} from '@xyflow/react';
import { useRef, useMemo, useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Button } from 'src/components/button';
import { useGlobalContext } from 'src/context/global/use-global-context';
import { useUndoRedo } from 'src/context/undo-redo';
import { syncNodeIpInterfacesAndValidate } from 'src/utils/node';
import { twMerge } from 'tailwind-merge';

import CustomEdgePath from './custom-edge-path';
import { DialogCustomEdgeEdit } from './dialog-edit';
import { createOffsetPath } from './edge-offset';
import './edge.scss';
import { EEdgeType, IEdgeExtraData } from './types';

export default function CustomEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  markerEnd,
  selected,
  label,
  animated,
  data,
  ...rest
}: EdgeProps) {
  const { vlans } = useGlobalContext();
  const { setEdges, setNodes, getEdges } = useReactFlow();
  const { takeSnapshot } = useUndoRedo();
  const currentEdges = getEdges();

  const [isEditing, setIsEditing] = useState(false);

  const isMultiSelected = useMemo(
    () => currentEdges.filter((edge) => edge.selected).length > 1,
    [currentEdges],
  );

  const edgeState = useMemo(() => {
    if (data?.edgeType === EEdgeType.STRAIGHT) {
      const [edgePath, labelX, labelY] = getStraightPath({
        sourceX,
        sourceY,
        targetX,
        targetY,
      });

      return {
        edgePath,
        labelX,
        labelY,
      };
    }
    if (data?.edgeType === EEdgeType.SMOOTHSTEP) {
      const [edgePath, labelX, labelY] = getSmoothStepPath({
        sourceX,
        sourceY,
        sourcePosition,
        targetX,
        targetY,
        targetPosition,
      });

      return {
        edgePath,
        labelX,
        labelY,
      };
    }
    const [edgePath, labelX, labelY] = getBezierPath({
      sourceX,
      sourceY,
      sourcePosition,
      targetX,
      targetY,
      targetPosition,
    });

    return {
      edgePath,
      labelX,
      labelY,
    };
  }, [
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
    data?.edgeType,
  ]);

  const [edgeColors, setEdgeColors] = useState<string[]>(['#b1b1b7']);
  const [editData, setEditData] = useState<IEdgeExtraData>(
    (data as IEdgeExtraData) || {},
  );
  const editDataRef = useRef(editData);
  useEffect(() => {
    editDataRef.current = editData;
  }, [editData]);

  const onRemoveEdge = () => {
    takeSnapshot();
    setEdges((edges) => {
      const newEdges = edges.filter((edge) => edge.id !== id);
      const edgeRemoved = edges.find((edge) => edge.id === id);
      if (edgeRemoved) {
        syncNodeIpInterfacesAndValidate({
          edgeRemoved,
          newEdges,
          setNodes,
          vlans,
        });
      }
      return newEdges;
    });
  };

  useEffect(() => {
    if (data?.vlanIds && (data?.vlanIds as number[]).length > 0) {
      const colors = ((data.vlanIds as number[]) || [])?.map(
        (vlanId: number) => {
          const vlan = vlans?.find(
            (c) =>
              parseInt(c.vlanId.toString(), 10) ===
              parseInt(vlanId.toString(), 10),
          );
          return vlan?.color || '#b1b1b7';
        },
      );
      setEdgeColors(colors.slice());
    } else {
      setEdgeColors(['#b1b1b7']);
    }
  }, [data, vlans]);

  useEffect(() => {
    setEditData((data as IEdgeExtraData) || {});
  }, [data]);

  const onChangeEdgeData = (newData: Partial<IEdgeExtraData>) => {
    setEditData((prev) => ({
      ...prev,
      ...newData,
    }));
  };

  const onSaveEdgeData = (newData: Partial<IEdgeExtraData>) => {
    takeSnapshot();
    setEdges((edges) => {
      const newEdges = edges.map((edge) => {
        if (edge.id === id) {
          return {
            ...edge,
            data: {
              ...(edge.data as object),
              ...newData,
            },
          };
        }
        return edge;
      });
      if (newData.vlanIds) {
        syncNodeIpInterfacesAndValidate({
          edgeId: id,
          newEdges,
          setNodes,
          vlans,
        });
      }
      return newEdges;
    });
    toast.success('Connection settings saved successfully');
  };

  const strokeWidth = useMemo(() => {
    const edgeCount = edgeColors.length;
    if (edgeCount > 10) return 0.5;
    const STEP = 0.25;
    const BASE_WIDTH = 3;
    const normalizedCount = Math.max(edgeCount, 1);
    return BASE_WIDTH - (normalizedCount - 1) * STEP;
  }, [edgeColors]);

  const selectedPath = useMemo(
    () =>
      createOffsetPath(
        edgeState.edgePath,
        0,
        (data as IEdgeExtraData | undefined)?.edgeType,
        {
          sourceX,
          sourceY,
          sourcePosition,
          targetX,
          targetY,
          targetPosition,
        },
      ),
    [
      edgeState.edgePath,
      data,
      sourceX,
      sourceY,
      sourcePosition,
      targetX,
      targetY,
      targetPosition,
    ],
  );

  return (
    <>
      {selected && (
        <BaseEdge
          id={`${id}-selected`}
          key={`${id}-selected`}
          path={selectedPath}
          markerEnd={markerEnd}
          style={{
            ...style,
            strokeWidth: edgeColors.length * strokeWidth + strokeWidth,
            stroke: '#3b82f6',
            strokeDasharray: 'unset',
            animation: 'none',
          }}
        />
      )}
      <CustomEdgePath
        id={id}
        edgeState={edgeState}
        edgeColors={edgeColors}
        markerEnd={markerEnd}
        style={style}
        strokeWidth={strokeWidth}
        edgeType={(data as IEdgeExtraData | undefined)?.edgeType}
        smoothStepParams={{
          sourceX,
          sourceY,
          sourcePosition,
          targetX,
          targetY,
          targetPosition,
        }}
      />
      {/* Invisible overlay for double-click handling */}
      <path
        d={edgeState.edgePath}
        stroke="transparent"
        strokeWidth={edgeColors.length * strokeWidth}
        fill="none"
        style={{
          cursor: 'pointer',
          strokeDasharray: 'unset',
          animation: 'none',
        }}
        onDoubleClick={(event: React.MouseEvent) => {
          event.preventDefault();
          event.stopPropagation();
          setIsEditing(true);
        }}
      />
      <EdgeLabelRenderer>
        {/* {!selected && (
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${edgeState.labelX}px,${edgeState.labelY}px)`,
              fontSize: 12,
              // everything inside EdgeLabelRenderer has no pointer events by default
              // if you have an interactive element, set pointer-events: all
              pointerEvents: 'all',
              zIndex: 20,
            }}
            className={twMerge([
              'standard-node-edit-edge',
              'flex items-center justify-center gap-2 rounded border border-gray-300 bg-gray-100 p-0.5',
              !selected && !label && 'hidden',
            ])}
          >
            <span className="text-sm text-gray-900">{label}</span>
          </div>
        )} */}
        {!isMultiSelected && selected && (
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${edgeState.labelX}px,${edgeState.labelY}px)`,
              fontSize: 12,
              // everything inside EdgeLabelRenderer has no pointer events by default
              // if you have an interactive element, set pointer-events: all
              pointerEvents: 'all',
              zIndex: 20,
            }}
            data-testid={`edge-toolbar-actions-${id}`}
            className={twMerge([
              'standard-node-edit-edge',
              'flex items-center justify-center gap-2 rounded border border-gray-300 bg-gray-100 p-0.5',
              !selected && !label && 'hidden',
            ])}
          >
            <div className="flex flex-row items-center justify-center gap-1">
              <Button
                data-tooltip-id="app-tooltip"
                data-tooltip-content="Edit"
                color="blue"
                data-testid={`edge-edit-button-${id}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsEditing(true);
                }}
                onDoubleClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <PencilSquareIcon className="h-4 w-4" />
              </Button>
              <Button
                data-tooltip-id="app-tooltip"
                data-tooltip-content="Delete"
                color="red"
                data-testid={`edge-delete-button-${id}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onRemoveEdge();
                }}
                onDoubleClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <TrashIcon className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </EdgeLabelRenderer>
      {isEditing && (
        <DialogCustomEdgeEdit
          ids={[id]}
          onCancel={() => {
            setIsEditing(false);
            setEditData((data as IEdgeExtraData) || {});
          }}
          data={editData}
          onChangeEdgeData={onChangeEdgeData}
          onSave={onSaveEdgeData}
        />
      )}
    </>
  );
}
